import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  IconButton,
  Switch,
  Text,
} from '@chakra-ui/react';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { IMAGE_EDITED } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { ChangeEvent, MouseEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { colors } from 'theme/colors';
import { shadows } from 'theme/shadows';
import { useEditor, useWidget } from 'widgets/sdk';

import { ImageWidgetData } from '../ImageWidget.types';
import { toggleAccordionExpand } from './ImageWidgetSideMenuSettings.helpers';
import ColorOverlay from './components/ColorOverlay';
import Filters from './components/Filters';

const FILTER_ACCORDION_INDEX = 0;
const COLOR_OVERLAY_ACCORDION_INDEX = 1;

const FilterResetButton = ({ onClickReset }: Readonly<{ onClickReset: () => void }>) => {
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'imageToolbar', useSuspense: false });
  const {
    filter: {
      effect = {},
      appliedDropShadowName = 'None',
      appliedFilterName = 'None',
      colorOverlay: { isEnabled = false } = {},
    } = {},
  } = useWidget<ImageWidgetData>();

  const showResetButton =
    appliedFilterName !== 'None' ||
    appliedDropShadowName !== 'None' ||
    Boolean(isEnabled) ||
    Object.values(effect).filter((v) => v !== 0).length > 0;

  return showResetButton ? (
    <Button variant='outline' onClick={onClickReset} color={colors.font[500]} ml={'auto'} mr={8} size='xs'>
      {t('settingsSideBar.returnToOriginal')}
    </Button>
  ) : (
    <></>
  );
};

export const ImageWidgetSideMenuSettings = () => {
  const { closeWidgetSideMenu } = useEditor();
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'imageToolbar', useSuspense: false });
  const { filter = {}, updateWidget } = useWidget<ImageWidgetData>();
  const { colorOverlay: { isEnabled: isOverlayEnabled = false } = {} } = filter;
  const [expandedIndex, setExpandedIndex] = useState<number[]>(isOverlayEnabled ? [0, 1] : [0]);

  useEffect(() => {
    Mixpanel.track(IMAGE_EDITED.eventName, {
      image_effect: IMAGE_EDITED.image_effect.ColorOverlay,
      styling_value: expandedIndex.includes(COLOR_OVERLAY_ACCORDION_INDEX)
        ? IMAGE_EDITED.color_overlay.On
        : IMAGE_EDITED.color_overlay.Off,
    });
  }, [expandedIndex]);

  const handleClose = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      closeWidgetSideMenu();
    },
    [closeWidgetSideMenu],
  );

  const handleDoubleClick = useCallback((e: MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleClickFilterSetting = () => {
    setExpandedIndex(toggleAccordionExpand(expandedIndex, FILTER_ACCORDION_INDEX));
  };

  const handleClickOverlaySetting = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setExpandedIndex(toggleAccordionExpand(expandedIndex, COLOR_OVERLAY_ACCORDION_INDEX));

    updateWidget({
      filter: {
        colorOverlay: {
          isEnabled: !isOverlayEnabled,
        },
      },
    });
  };

  const handleChangeSwitchOverlay = (e: ChangeEvent<HTMLInputElement>) => {
    setExpandedIndex(toggleAccordionExpand(expandedIndex, COLOR_OVERLAY_ACCORDION_INDEX));

    updateWidget({
      filter: {
        colorOverlay: {
          isEnabled: !e.target.checked,
        },
      },
    });
  };

  const handleClickReset = useCallback(() => {
    // Only toggle if color overlay is enabled
    if (isOverlayEnabled) {
      setExpandedIndex(toggleAccordionExpand(expandedIndex, COLOR_OVERLAY_ACCORDION_INDEX));
    }

    updateWidget({
      filter: {
        appliedDropShadowName: 'None',
        appliedFilterName: 'None',
        effect: {
          brightness: 0,
          contrast: 0,
          saturation: 0,
          tint: 0,
        },
        filterIntensity: 50,
        dropShadowIntensity: 0,
        colorOverlay: {
          isEnabled: false,
        },
      },
    });

    Mixpanel.track(IMAGE_EDITED.eventName, {
      image_effect: IMAGE_EDITED.image_effect.ReturnToOriginal,
    });
  }, [updateWidget, expandedIndex, isOverlayEnabled]);

  return (
    <>
      <Flex
        borderTop={`8px solid ${colors.upgrade.blue[500]}`}
        borderBottom={`1px solid ${colors.gray[100]}`}
        h={12}
        placeItems={'center'}
        p={4}
        mb={4}
        onDoubleClick={handleDoubleClick} // NOTE: Fix double click will cause to show replace menu
      >
        <Box fontSize='medium' fontWeight='bold' flex='1' textAlign='left' textTransform={'capitalize'}>
          {t('settingsSideBar.imageFilters')}
        </Box>
        <FilterResetButton onClickReset={handleClickReset} />
        <IconButton
          size='xs'
          aria-label={'Close menu'}
          bg='transparent'
          position='absolute'
          right='0'
          m='10px'
          icon={<CloseIcon />}
          onClick={handleClose}
        />
      </Flex>
      <Accordion
        boxShadow='settingsAccordion'
        allowMultiple
        index={expandedIndex}
        shadow={'none'}
        pb={60}
        maxHeight={'calc(100vh - 124px)'}
        overflowY={'auto'}
      >
        <AccordionItem borderRadius='base' mx={4} shadow={shadows.settingsAccordion} onDoubleClick={handleDoubleClick}>
          <h2>
            <AccordionButton paddingInlineEnd={4} paddingInlineStart={4} py={3} onClick={handleClickFilterSetting}>
              <Box as='span' flex='1' textAlign='left'>
                <Text fontSize={'sm'} fontWeight='bold' w={'full'} display='block' alignSelf={'start'}>
                  {t('settingsSideBar.filter')}
                </Text>
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel paddingInline={0} onDoubleClick={handleDoubleClick}>
            <Filters />
          </AccordionPanel>
        </AccordionItem>
        <AccordionItem
          borderRadius='base'
          mx={4}
          mt={4}
          shadow={shadows.settingsAccordion}
          onDoubleClick={handleDoubleClick}
        >
          <h2>
            <AccordionButton paddingInlineEnd={4} paddingInlineStart={4} py={3} onClick={handleClickOverlaySetting}>
              <Box as='span' flex='1' textAlign='left'>
                <Text fontSize={'sm'} fontWeight='bold' w={'full'} display='block' alignSelf={'start'}>
                  {t('settingsSideBar.overlay')}
                </Text>
              </Box>
              <Switch onChange={handleChangeSwitchOverlay} isChecked={isOverlayEnabled} />
            </AccordionButton>
          </h2>
          <AccordionPanel paddingInline={0} onDoubleClick={handleDoubleClick}>
            <ColorOverlay />
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </>
  );
};

export default ImageWidgetSideMenuSettings;
